import { EntityManager } from "game-common/entity_manager"
import { Entity, Coordinate, TileSize } from "game-common/models"
import Quadtree, { Rect } from "game-common/quadtree"
import { ScalingParams } from "../../../client_util"

export interface ViewportBounds {
    x1: number
    y1: number
    x2: number
    y2: number
    width: number
    height: number
}

export interface CachedVisibleEntities {
    entities: Array<{ entity: Entity; distSquared: number }>
    playerPos: Coordinate
    timestamp: number
    viewportBounds: ViewportBounds
}

export class ClientViewportManager {
    private entityManager: EntityManager<Entity>
    private viewportQuadTree: Quadtree
    private cachedVisibleEntities: CachedVisibleEntities | null = null
    private lastPlayerPos: Coordinate | null = null
    private cacheValid = false
    private worldWidth: number
    private worldHeight: number

    // Reprioritize moving visible entities at most four times per second.
    private readonly SORT_INTERVAL_MS = 250
    private prioritiesChanged = false
    private visibleEntities = new Map<string, Entity>()
    private readonly MOVEMENT_THRESHOLD = 16 // 16 pixels movement threshold

    // Entity movement tracking for efficient updates
    private entityRects = new Map<string, Rect>()

    // Store bound listener references for proper cleanup
    private boundUpdateListener: (entity: Entity) => void
    private boundRemoveListener: (entity: Entity) => void

    // Performance tracking
    private cacheHits = 0
    private cacheMisses = 0
    private totalQueries = 0

    constructor(entityManager: EntityManager<Entity>, worldWidth: number, worldHeight: number) {
        this.entityManager = entityManager
        this.worldWidth = worldWidth
        this.worldHeight = worldHeight

        // Store bound listeners for proper cleanup
        this.boundUpdateListener = this.onEntityUpdate.bind(this)
        this.boundRemoveListener = this.onEntityRemove.bind(this)

        // Initialize quadtree for the entire world
        this.viewportQuadTree = new Quadtree(
            {
                x: 0,
                y: 0,
                width: worldWidth,
                height: worldHeight,
            },
            10,
            6,
        ) // 10 objects per node, 6 levels deep

        // Register entity listeners for efficient updates
        this.setupEntityListeners()

        // Initial population of quadtree
        this.populateQuadTree()
    }

    private setupEntityListeners(): void {
        // Listen for entity updates to keep quadtree in sync
        this.entityManager.registerSetListener(this.boundUpdateListener)
        this.entityManager.registerRemoveListener(this.boundRemoveListener)
    }

    private onEntityUpdate(entity: Entity): void {
        // Skip entities without valid location or entityId
        if (!entity || !entity.location || !entity.entityId) {
            return
        }

        const oldRect = this.entityRects.get(entity.entityId)
        const width = entity.dimensions?.w || TileSize
        const height = entity.dimensions?.h || TileSize
        const x = entity.location.x - width / 2
        const y = entity.location.y - height / 2
        const moved =
            !oldRect || oldRect.x !== x || oldRect.y !== y ||
            oldRect.width !== width || oldRect.height !== height

        if (moved) {
            if (oldRect) this.viewportQuadTree.remove(oldRect)
            this.addEntityToQuadTree(entity)
        }

        const bounds = this.cachedVisibleEntities?.viewportBounds
        if (!bounds) return
        const wasVisible = this.visibleEntities.has(entity.entityId)
        const isVisible = this.isEntityInViewport(entity, bounds)
        if (wasVisible !== isVisible) {
            this.invalidateCache()
        } else if (isVisible) {
            // Keep the cached references current if an entity object was replaced.
            if (this.visibleEntities.get(entity.entityId) !== entity) {
                this.invalidateCache()
            } else if (moved) {
                this.prioritiesChanged = true
            }
        }
    }

    private onEntityRemove(entity: Entity): void {
        if (!entity?.entityId) return
        const rect = this.entityRects.get(entity.entityId)
        if (rect) this.viewportQuadTree.remove(rect)
        this.entityRects.delete(entity.entityId)
        if (this.visibleEntities.has(entity.entityId)) this.invalidateCache()
    }

    private populateQuadTree(): void {
        this.viewportQuadTree.clear()
        this.entityRects.clear()
        this.entityManager.iterate(null, (entity: Entity) => this.addEntityToQuadTree(entity))
    }

    private addEntityToQuadTree(entity: Entity): void {
        if (!entity?.location || !entity.entityId) return
        const width = entity.dimensions?.w || TileSize
        const height = entity.dimensions?.h || TileSize
        const rect: Rect = {
            x: entity.location.x - width / 2,
            y: entity.location.y - height / 2,
            width,
            height,
            entityId: entity.entityId,
        }
        this.entityRects.set(entity.entityId, rect)
        this.viewportQuadTree.insert(rect)
    }

    private invalidateCache(): void {
        this.cacheValid = false
        this.cachedVisibleEntities = null
    }

    // The renderer uses this to decide whether to replace its cached render list.
    needsVisibleEntitiesRefresh(playerPos: Coordinate): boolean {
        return !this.cacheValid || !this.isCacheStillValid(playerPos)
    }

    getVisibleEntities(
        playerPos: Coordinate,
        viewportSize?: { width: number; height: number },
    ): Array<{ entity: Entity; distSquared: number }> {
        this.totalQueries++

        // Check if cache is still valid
        if (!this.needsVisibleEntitiesRefresh(playerPos)) {
            this.cacheHits++
            return this.cachedVisibleEntities!.entities
        }

        this.cacheMisses++

        // Calculate viewport bounds
        const viewportBounds = this.calculateViewportBounds(playerPos, viewportSize)

        // Use quadtree to find entities in viewport area - MUCH faster than iterating all entities
        const foundRects = this.viewportQuadTree.retrieve({
            x: viewportBounds.x1 - TileSize / 2,
            y: viewportBounds.y1 - TileSize / 2,
            width: viewportBounds.width + TileSize,
            height: viewportBounds.height + TileSize,
        })

        // Convert rects to entities and filter to actual visible entities
        const entitiesWithDistance: Array<{ entity: Entity; distSquared: number }> = []

        for (const rect of foundRects) {
            if (!rect.entityId) continue

            const entity = this.entityManager.get(rect.entityId)
            if (!entity) continue

            // Additional viewport bounds check for precision
            if (this.isEntityInViewport(entity, viewportBounds)) {
                // Calculate distance for sorting
                const dx = entity.location.x - playerPos.x
                const dy = entity.location.y - playerPos.y
                const distSquared = dx * dx + dy * dy

                entitiesWithDistance.push({ entity, distSquared })
            }
        }

        // Sort by distance (closest first)
        entitiesWithDistance.sort((a, b) => a.distSquared - b.distSquared)

        // Cache the result
        this.cachedVisibleEntities = {
            entities: entitiesWithDistance,
            playerPos: { x: playerPos.x, y: playerPos.y },
            timestamp: Date.now(),
            viewportBounds,
        }
        this.visibleEntities = new Map(entitiesWithDistance.map(next => [next.entity.entityId, next.entity]))
        this.prioritiesChanged = false
        this.cacheValid = true
        this.lastPlayerPos = { x: playerPos.x, y: playerPos.y }

        return entitiesWithDistance
    }

    private isCacheStillValid(playerPos: Coordinate): boolean {
        if (!this.cachedVisibleEntities || !this.lastPlayerPos) {
            return false
        }

        // Static scenes keep their list indefinitely; only moving bodies need reprioritizing.
        if (this.prioritiesChanged && Date.now() - this.cachedVisibleEntities.timestamp >= this.SORT_INTERVAL_MS) {
            return false
        }

        // Check if player moved significantly
        const dx = Math.abs(playerPos.x - this.lastPlayerPos.x)
        const dy = Math.abs(playerPos.y - this.lastPlayerPos.y)

        return dx <= this.MOVEMENT_THRESHOLD && dy <= this.MOVEMENT_THRESHOLD
    }

    private calculateViewportBounds(
        playerPos: Coordinate,
        viewportSize?: { width: number; height: number },
    ): ViewportBounds {
        const X_FACTOR = ScalingParams.viewportX
        const Y_FACTOR = ScalingParams.viewportY - 1

        // Use provided viewport size or default
        const viewportWidth = viewportSize?.width || X_FACTOR * 2 * TileSize
        const viewportHeight = viewportSize?.height || Y_FACTOR * 2 * TileSize

        // Calculate viewport center
        let viewportCenterX = playerPos.x
        let viewportCenterY = playerPos.y

        // Handle edge cases (player at map boundaries)
        const inBoundsX = playerPos.x > TileSize * X_FACTOR && playerPos.x < (this.worldWidth - X_FACTOR) * TileSize
        const inBoundsY = playerPos.y > TileSize * Y_FACTOR && playerPos.y < (this.worldHeight - Y_FACTOR) * TileSize

        if (!inBoundsX) {
            if (playerPos.x <= TileSize * X_FACTOR) {
                viewportCenterX = TileSize * X_FACTOR
            } else {
                viewportCenterX = (this.worldWidth - X_FACTOR) * TileSize
            }
        }

        if (!inBoundsY) {
            if (playerPos.y <= TileSize * Y_FACTOR) {
                viewportCenterY = TileSize * Y_FACTOR
            } else {
                viewportCenterY = (this.worldHeight - Y_FACTOR) * TileSize
            }
        }

        const x1 = viewportCenterX - viewportWidth / 2
        const y1 = viewportCenterY - viewportHeight / 2
        const x2 = viewportCenterX + viewportWidth / 2
        const y2 = viewportCenterY + viewportHeight / 2

        return {
            x1,
            y1,
            x2,
            y2,
            width: viewportWidth,
            height: viewportHeight,
        }
    }

    private isEntityInViewport(entity: Entity, viewportBounds: ViewportBounds): boolean {
        const entityX = entity.location.x
        const entityY = entity.location.y

        // Add small padding for entities that might be partially visible
        const padding = TileSize / 2

        return (
            entityX >= viewportBounds.x1 - padding &&
            entityX <= viewportBounds.x2 + padding &&
            entityY >= viewportBounds.y1 - padding &&
            entityY <= viewportBounds.y2 + padding
        )
    }

    /**
     * Force cache invalidation (useful when world changes)
     */
    forceInvalidateCache(): void {
        this.invalidateCache()
    }

    /**
     * Reset the viewport manager for a new room - clears all data but keeps the instance
     * More efficient than destroying and recreating
     */
    reset(newWorldWidth?: number, newWorldHeight?: number): void {
        // Update world dimensions if provided
        if (newWorldWidth !== undefined) {
            this.worldWidth = newWorldWidth
        }
        if (newWorldHeight !== undefined) {
            this.worldHeight = newWorldHeight
        }

        // Clear all data
        this.viewportQuadTree.clear()
        this.entityRects.clear()
        this.visibleEntities.clear()
        this.prioritiesChanged = false
        this.cachedVisibleEntities = null
        this.cacheValid = false
        this.lastPlayerPos = null

        // Reset performance counters
        this.cacheHits = 0
        this.cacheMisses = 0
        this.totalQueries = 0

        // Repopulate with current entities
        this.populateQuadTree()
    }

    /**
     * Get performance statistics
     */
    getPerformanceStats(): {
        cacheHits: number
        cacheMisses: number
        totalQueries: number
        hitRate: number
        quadtreeSize: number
    } {
        const hitRate = this.totalQueries > 0 ? (this.cacheHits / this.totalQueries) * 100 : 0

        return {
            cacheHits: this.cacheHits,
            cacheMisses: this.cacheMisses,
            totalQueries: this.totalQueries,
            hitRate: Math.round(hitRate * 100) / 100,
            quadtreeSize: this.entityRects.size,
        }
    }

    /**
     * Cleanup resources
     */
    destroy(): void {
        // Properly unregister listeners using stored references
        this.entityManager.unregisterSetListener(this.boundUpdateListener)
        this.entityManager.unregisterRemoveListener(this.boundRemoveListener)
        this.viewportQuadTree.clear()
        this.entityRects.clear()
        this.visibleEntities.clear()
        this.prioritiesChanged = false
        this.cachedVisibleEntities = null
        this.cacheValid = false
    }
}
